import { useSelector } from 'react-redux';
import Logger from 'shared/utils/Logger';
import { getLang } from 'shared/store/selectors/lang.selector';
import styles from './render-binder-selector.module.scss';
import { Button, Divider, Modal, SelectBox } from 'src/components/design-system';
import { getSelectedWorkspaceId, getWorkspaces } from 'shared/store/selectors/workspace.selector';
import { getPolicyTechWorkspaceIds } from 'shared/store/selectors/knowledge.selector';
import { useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { CATEGORY_TYPES, colorPalette, STORE_ENTITY_TYPES } from 'shared/constants';
import { SectionComponent } from 'src/components/common/section/section.component';
import { CategoryRowComponent } from 'src/components/common/categories/category-row';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faHome } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { NoContent } from 'src/components/common/noContent/NoContent.component';
import { MirrorBadge } from 'src/pages/application/knowledge/components/mirror/mirror-badge.component';

const ENTITY_TO_CATEGORY_TYPE = {
  [STORE_ENTITY_TYPES.KNOWLEDGE]: CATEGORY_TYPES.KNOWLEDGE,
  [STORE_ENTITY_TYPES.ADMISSION]: CATEGORY_TYPES.CHECKS,
  [STORE_ENTITY_TYPES.LAB]: CATEGORY_TYPES.VALUES,
};

export function RenderBinderSelector({
  pathParts,
  parentId,
  title,
  subtitle,
  submitButtonText,
  selectTarget,
  onSubmit,
  isOpen,
  onClose,
  filterCategories,
  children = null,
  entity = STORE_ENTITY_TYPES.KNOWLEDGE,
  generateBreadcrumbs = () => {},
  getCategoriesById = () => {},
  getCategoryById = () => {},
  getRoot = () => {},
  isCategoryMove = false,
  isSection = () => {},
  selectText,
  selectedText,
  selectRootText,
  submitDisabled: externalSubmitDisabled = false,
  onWorkspaceChange,
  defaultWorkspaceId,
  excludeWorkspaceIds = [],
  disabledWorkspaceIds = [],
  disabledWorkspaceTooltip,
  alreadyInCategoryText,
  cannotMoveToBinderText,
  blockMirroredDestinations = false,
}) {
  const [binderId, setBinderId] = useState(null);
  const lang = useSelector(getLang('BINDER_SELECTOR'));
  const mirrorLang = useSelector(getLang('KNOWLEDGE_CATEGORY'));
  const [loading, setLoading] = useState(false);
  const workspaces = useSelector(getWorkspaces);
  const currentWorkspaceId = useSelector(getSelectedWorkspaceId);
  const policyTechWorkspaceIds = useSelector(getPolicyTechWorkspaceIds);
  const preferredWorkspaceId =
    defaultWorkspaceId !== undefined ? defaultWorkspaceId : currentWorkspaceId;
  // A PolicyTech workspace must not be preselected either, or its tree would still render.
  const initialWorkspaceId = policyTechWorkspaceIds.includes(preferredWorkspaceId)
    ? null
    : preferredWorkspaceId;
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(initialWorkspaceId);
  const onSelectWorkspace = workspaceId => {
    setSelectedWorkspaceId(workspaceId);
    onWorkspaceChange?.(workspaceId);
  };

  const isSectionMove = isCategoryMove && isSection(pathParts);
  const isBinderOrDirectoryMove = isCategoryMove && !isSection(pathParts);

  const workspacesOptions = useMemo(() => {
    return workspaces
      .filter(({ id }) => !excludeWorkspaceIds.includes(id) && !policyTechWorkspaceIds.includes(id))
      .map(({ id, name }) => {
        const isDisabled = disabledWorkspaceIds.includes(id);
        return {
          value: id,
          label: name,
          disabled: isDisabled,
          title: isDisabled ? disabledWorkspaceTooltip : undefined,
        };
      });
  }, [
    workspaces,
    excludeWorkspaceIds,
    policyTechWorkspaceIds,
    disabledWorkspaceIds,
    disabledWorkspaceTooltip,
  ]);

  const rootCategory = useSelector(state =>
    selectedWorkspaceId ? getRoot(state[entity], selectedWorkspaceId) : null,
  );
  const [categoryId, setCategoryId] = useState(rootCategory?.id);

  useEffect(() => {
    setCategoryId(rootCategory?.id);
    setBinderId(null);
  }, [rootCategory?.id, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedWorkspaceId(initialWorkspaceId);
    }
  }, [isOpen, initialWorkspaceId]);

  const category = useSelector(state => {
    if (!categoryId) return null;
    const result = getCategoryById(state[entity], { id: categoryId });
    return result?.id ? result : null;
  });
  const sections = useSelector(state =>
    category ? getCategoriesById(state[entity], category.id, selectedWorkspaceId) : null,
  );
  const subcategories = useSelector(state =>
    sections?.reduce(
      (acc, section) => ({
        ...acc,
        [section.id]: section.subcategories?.map(id => getCategoryById(state[entity], { id })),
      }),
      {},
    ),
  );

  const onSelectCategory = selectedId => {
    setCategoryId(selectedId);
  };

  const onSelectTarget = selectedId => {
    setBinderId(selectedId);
    selectTarget({ binderId: selectedId, targetWorkspaceId: selectedWorkspaceId });
  };

  const breadcrumbs = useSelector(state =>
    category
      ? generateBreadcrumbs(state[entity], {
          pathParts: category.path.split(','),
          initialWorkspaceId: selectedWorkspaceId,
        })
      : [],
  );

  const renderRootBreadcrumb = () => (
    <button
      type="button"
      key={rootCategory.id}
      onClick={() => setCategoryId(rootCategory.id)}
      className={styles.breadcrumbButton}
      aria-label={lang.WORKSPACE}>
      <FontAwesomeIcon icon={faHome} aria-hidden="true" />
    </button>
  );

  const showBreadcrumbs = rootCategory && categoryId !== rootCategory.id;

  const renderBreadcrumbs = () => (
    <nav aria-label="breadcrumb" className={styles.breadcrumbs}>
      {breadcrumbs.length ? (
        <>
          {renderRootBreadcrumb()}
          {breadcrumbs
            .filter(item => !isSection(item.path.split(',')))
            .map(item => (
              <div className={styles.afterHomeBreadcrumbs} key={item.id}>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className={styles.separator}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  onClick={() => setCategoryId(item.id)}
                  className={styles.breadcrumbButton}>
                  {item.title}
                </button>
              </div>
            ))}
        </>
      ) : null}
    </nav>
  );

  const getSelectButtonText = (id, isMoveToRoot) => {
    if (isMoveToRoot) return selectRootText || lang.SELECT_ROOT;
    if (binderId === id) return selectedText || lang.SELECTED;
    return selectText || lang.SELECT;
  };

  const renderSelectTargetButton = (id, isMoveToRoot = false) => (
    <Button
      onClick={e => {
        e.stopPropagation();
        onSelectTarget(id);
      }}
      type={binderId === id ? 'primary' : 'default'}
      disabled={parentId === id || loading}>
      {getSelectButtonText(id, isMoveToRoot)}
    </Button>
  );

  const renderCategoriesList = () => {
    if (!sections?.length) {
      return <NoContent text={lang.NO_SUBCATEGORIES} className={styles.noContent} />;
    }

    return (
      <div className={styles.categoriesContainer}>
        {sections.map(section => {
          const isSectionMirrored = blockMirroredDestinations && !!section?.mirroredFrom;
          const isSectionFiltered = filterCategories?.includes(section.id);
          const isSectionDisabled = isSectionMirrored || isSectionFiltered;
          const sectionTooltip = isSectionMirrored
            ? mirrorLang.CANNOT_CREATE_MIRROR_INSIDE_MIRROR
            : isSectionFiltered
              ? alreadyInCategoryText || lang.ALREADY_IN_CATEGORY
              : undefined;
          return (
            <SectionComponent
              key={section.id}
              title={section.title}
              defaultIsOpen={false}
              disabled={isSectionDisabled}
              tooltip={sectionTooltip}
              badge={isSectionMirrored ? <MirrorBadge /> : undefined}
              customActions={
                isBinderOrDirectoryMove && !isSectionDisabled
                  ? renderSelectTargetButton(section.id)
                  : undefined
              }>
              {subcategories[section.id]?.map(category => {
                const isBinder = category?.type !== CATEGORY_TYPES.CATEGORY;
                const onClick = isBinder ? onSelectTarget : onSelectCategory;
                const isCategoryMoveIntoBinder = isCategoryMove && isBinder;
                const isAlreadyInCategory = filterCategories?.includes(category.id);
                const isMirrored = !!category?.mirroredFrom;
                const isMirroredBlockedTarget =
                  blockMirroredDestinations && (isMirrored || isSectionMirrored);
                const isDisabled =
                  isCategoryMoveIntoBinder || isAlreadyInCategory || isMirroredBlockedTarget;

                const getDisabledReason = () => {
                  if (isMirroredBlockedTarget) {
                    return mirrorLang.CANNOT_CREATE_MIRROR_INSIDE_MIRROR;
                  }
                  if (isCategoryMoveIntoBinder) {
                    return cannotMoveToBinderText || lang.CANNOT_MOVE_TO_BINDER;
                  }
                  return isAlreadyInCategory
                    ? alreadyInCategoryText || lang.ALREADY_IN_CATEGORY
                    : undefined;
                };
                const disabledReason = getDisabledReason();

                const isSelected = binderId === category.id;

                return (
                  <div
                    key={category.id}
                    id={category.id}
                    title={disabledReason}
                    className={classNames(styles.row, {
                      [styles.disabled]: isDisabled,
                      [styles.rowSelected]: isSelected,
                    })}>
                    <CategoryRowComponent
                      {...category}
                      onClick={isDisabled ? undefined : () => onClick(category.id)}
                      categoryType={ENTITY_TO_CATEGORY_TYPE[entity]}
                      badge={
                        isMirroredBlockedTarget ? <MirrorBadge label={disabledReason} /> : undefined
                      }
                      actions={
                        isSectionMove && !isBinder ? renderSelectTargetButton(category.id) : null
                      }
                    />
                  </div>
                );
              })}
            </SectionComponent>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      title={<span className={styles.title}>{title}</span>}
      onCancel={onClose}
      destroyOnHidden
      width={800}
      submitButtonType="primary"
      submitDisabled={!binderId || externalSubmitDisabled}
      onSubmit={async () => {
        setLoading(true);
        try {
          await onSubmit();
          onClose();
        } catch (error) {
          // onSubmit must throw to signal failure; if it throws, the modal stays open so the user can retry
          Logger.error('[RenderBinderSelector] onSubmit failed:', { error });
        } finally {
          setLoading(false);
        }
      }}
      submitText={submitButtonText}
      loading={loading}>
      <div className={styles.container}>
        <p>{subtitle}</p>
        <Divider className={styles.divider} />
        <div className={styles.workspace}>
          <p>{lang.WORKSPACE}</p>
          <ConfigProvider
            theme={{
              components: {
                Select: {
                  colorText: colorPalette.primaryMain6,
                },
              },
            }}>
            <SelectBox
              placeholder={lang.SELECT_WORKSPACE}
              options={workspacesOptions}
              value={selectedWorkspaceId}
              onChange={(name, value) => onSelectWorkspace(value)}
              variant="borderless"
              size="small"
              showSearch={false}
            />
          </ConfigProvider>
        </div>
        {selectedWorkspaceId && <Divider className={styles.divider} />}

        {showBreadcrumbs && category && (
          <div className={styles.titleContainer}>
            {renderBreadcrumbs()}
            <span className={styles.title}>{category.title}</span>
          </div>
        )}
        {selectedWorkspaceId && (
          <>
            {isSectionMove && rootCategory && (
              <div className={styles.moveToRoot}>
                {renderSelectTargetButton(rootCategory.id, true)}
              </div>
            )}
            {renderCategoriesList()}
          </>
        )}
        {children}
      </div>
    </Modal>
  );
}
